/* global reportCentrePerPage */

import React, { useEffect, useState } from 'react';

import { format } from 'date-fns';

import { getDateFnsLocale } from 'Helpers/localeService';
import { apiClient } from 'Helpers/network';

import Badge from 'CommonComponents/Badge';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import usePagination from 'Hooks/usePagination';

// Generation outcome → Badge variant/label (matches the STATUS_BADGE convention
// on ScheduledReportsList, reusing the shared colour tokens via Badge variants).
const GENERATION_BADGE = {
    success: { variant: 'success', label: $t('Success') },
    failed: { variant: 'danger', label: $t('Failed') },
    skipped: { variant: 'neutral', label: $t('Skipped') },
    pending: { variant: 'info', label: $t('Pending') },
};

// Per-recipient delivery outcome → Badge variant/label.
const EMAIL_STATUS_BADGE = {
    delivered: { variant: 'success', label: $t('Delivered') },
    sent: { variant: 'neutral', label: $t('Sent') },
    bounced: { variant: 'danger', label: $t('Bounced') },
    failed: { variant: 'danger', label: $t('Failed') },
};

const formatDateTime = (value) => (value ? format(new Date(value), 'd MMM yyyy, HH:mm', { locale: getDateFnsLocale() }) : '-');

const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) {
        return '-';
    }
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    const kb = bytes / 1024;
    if (kb < 1024) {
        return `${kb.toFixed(1)} KB`;
    }
    return `${(kb / 1024).toFixed(1)} MB`;
};

/**
 * Delivery-history modal (IB-775, AC6): the paginated fire log for a scheduled
 * report. Mirrors ScheduleReportModal's shell (Modal + ModalHeader +
 * modal__form-container) and the Scheduled Reports list table styling
 * (inbud-table), showing each run's generation outcome, attachment size and the
 * per-recipient delivered/bounced/failed outcomes.
 */
export default function ScheduleDeliveryHistoryModal({ modal, runsUrl, schedule, nonce = 0 }) {
    // perPage is seeded from the blade-injected reportCentrePerPage global (backed by
    // config('constants.pagination.default_per_page')), so it stays in step with the
    // backend default automatically. Seed total: 0 so the pagination controls stay
    // hidden until a real (positive) total loads.
    const pagination = usePagination({ perPage: reportCentrePerPage, total: 0 });
    const [loading, setLoading] = useState(false);
    const [runs, setRuns] = useState([]);

    // Reset to the first page whenever the modal is (re)opened for a schedule.
    useEffect(() => {
        pagination.setPage(1);
    }, [nonce]);

    useEffect(() => {
        if (!schedule || !runsUrl) {
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        apiClient
            .get(runsUrl.replace('REPORT_SCHEDULE_ID', schedule.id), { params: { page: pagination.page, per_page: pagination.perPage } })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const body = response.data || {};
                const meta = body.meta || {};
                setRuns(body.data || []);
                pagination.setFrom(meta.from ?? 0);
                pagination.setTo(meta.to ?? 0);
                pagination.setTotal(meta.total ?? 0);
                pagination.setLastPage(meta.last_page ?? 1);
            })
            .catch(() => {
                if (!cancelled) {
                    setRuns([]);
                    pagination.setTotal(0);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [schedule, runsUrl, pagination.page, nonce]);

    const close = () => modal.hide();
    const columnCount = 4;

    return (
        <Modal modal={modal} modalIndexClass="tw-z-60" parentContainerClasses="tw-max-w-3xl">
            <ModalHeader title={$t('Delivery history')} subtitle={schedule?.name} close={close} />

            <div className="modal__form-container tw-space-y-4">
                {pagination.total > 0 && (
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <div className="table__pagination-info">
                            <PaginationInfo from={pagination.from} to={pagination.to} total={pagination.total} />
                            <TablePagination
                                lastPage={pagination.lastPage}
                                pageChange={(data) => pagination.setPage(+data.selected + 1)}
                                currentPage={pagination.page}
                            />
                        </div>
                    </div>
                )}

                <div className="inbud-table_wrapper">
                    <table className="inbud-table">
                        <thead>
                            <tr>
                                <th className="tw-w-44 tw-min-w-44">{$t('Run time')}</th>
                                <th className="tw-w-32 tw-min-w-32">{$t('Outcome')}</th>
                                <th className="tw-w-28 tw-min-w-28">{$t('Size')}</th>
                                <th>{$t('Recipients')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <TableRowSkeleton rows={4} columns={columnCount} />}

                            {!loading &&
                                runs.map((run) => {
                                    const badge = GENERATION_BADGE[run.generation_status] || GENERATION_BADGE.pending;

                                    return (
                                        <tr key={run.id}>
                                            <td className="inbud-date-cell">{formatDateTime(run.run_at)}</td>
                                            <td>
                                                <Badge variant={badge.variant}>{badge.label}</Badge>
                                                {run.error && <div className="tw-mt-1 tw-text-2xs tw-text-red-600">{run.error}</div>}
                                            </td>
                                            <td className="tw-text-sm tw-text-charcoal-75">{formatBytes(run.attachment_size)}</td>
                                            <td>
                                                {(run.email_status_summary || []).length === 0 ? (
                                                    <span className="tw-text-sm tw-text-charcoal-75">-</span>
                                                ) : (
                                                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                                        {(run.email_status_summary || []).map((entry, index) => {
                                                            const statusBadge = EMAIL_STATUS_BADGE[entry.status] || EMAIL_STATUS_BADGE.sent;

                                                            return (
                                                                <span
                                                                    key={`${entry.email}-${index}`}
                                                                    className="tw-inline-flex tw-items-center tw-gap-1.5"
                                                                    title={entry.email}
                                                                >
                                                                    <span className="tw-text-2xs tw-text-charcoal-75">{entry.email}</span>
                                                                    <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}

                            {!loading && runs.length === 0 && (
                                <tr>
                                    <td colSpan={columnCount}>
                                        <div className="tw-py-12 tw-text-center">
                                            <div className="tw-font-medium tw-text-charcoal">{$t('No deliveries yet')}</div>
                                            <div className="tw-mt-1.5 tw-text-sm tw-text-charcoal-75">
                                                {$t('This schedule has not run yet. Delivery outcomes will appear here after the first run.')}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
}
